import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { db } from "../firebase/config";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  updateDoc,
} from "firebase/firestore";
import {
  getSlaPolicy,
  ensureTicketSla,
  markFirstResponseIfNeeded,
  enterPause,
  exitPause,
  markResolved,
} from "../utils/sla";

import { useAuth } from "../Context/AuthContext";
import "../styles/TicketDetails.css";

// --- tiny helper for nice ETA
const _ms = (d) => (d instanceof Date ? d.getTime() : new Date(d).getTime());
const etaLabel = (dueAt) => {
  if (!dueAt) return "—";
  const diff = _ms(dueAt) - Date.now();
  const absMin = Math.round(Math.abs(diff) / 60000);
  if (absMin < 60) return diff >= 0 ? `${absMin}m` : `${absMin}m overdue`;
  const h = Math.floor(absMin / 60);
  const m = absMin % 60;
  return diff >= 0 ? `${h}h ${m}m` : `${h}h ${m}m overdue`;
};

export default function TicketDetails() {
  const { ticketId } = useParams(); // Get ticket ID from URL
  const { user } = useAuth(); // Get current user
  const [companyId, setCompanyId] = useState(null);
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updating, setUpdating] = useState(false);

  // 🔹 NEW: local editable priority
  const [priority, setPriority] = useState("Low");

  const [helpdeskComment, setHelpdeskComment] = useState("");
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [slaPolicy, setSlaPolicy] = useState(null);

  // ✅ Fetch Employee's Company ID
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError("");

        if (!user) throw new Error("User not authenticated.");

        // ✅ Employers/owners
        if (user.companyId) {
          setCompanyId(user.companyId);
          return;
        }

        // ✅ Fallback for employees
        const companiesSnapshot = await getDocs(collection(db, "companies"));
        for (const companyDoc of companiesSnapshot.docs) {
          const employeeDoc = await getDoc(
            doc(db, `companies/${companyDoc.id}/employees`, user.uid)
          );
          if (employeeDoc.exists()) {
            setCompanyId(companyDoc.id);
            return;
          }
        }

        setError("Company not found.");
      } catch (err) {
        setError("Failed to fetch company ID.");
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  useEffect(() => {
    if (!companyId) return;
    (async () => {
      const p = await getSlaPolicy(companyId);
      setSlaPolicy(p);
    })();
  }, [companyId]);

  // ✅ Fetch Ticket Details
  useEffect(() => {
    if (!companyId) return;

    const fetchTicket = async () => {
      try {
        setLoading(true);
        setError("");

        const ticketRef = doc(db, `companies/${companyId}/tickets`, ticketId);
        const ticketSnap = await getDoc(ticketRef);

        if (ticketSnap.exists()) {
          const t = ticketSnap.data();
          setTicket(t);
          setHelpdeskComment(t.helpdeskComment || "");
          setResolutionNotes(t.resolutionNotes || "");

          // 🔹 NEW: populate editable priority
          setPriority(t.priority || "Low");

          // 🔁 Initialize or refresh SLA due-times on this ticket
          try {
            const p = slaPolicy || (await getSlaPolicy(companyId));
            setSlaPolicy(p);
            if (p) {
              await ensureTicketSla(companyId, ticketId, t, p);
            }
          } catch (e) {
            console.warn("SLA init failed:", e);
          }
        } else {
          setError("Ticket not found.");
        }
      } catch (err) {
        console.error("Error fetching ticket:", err.message);
        setError("Failed to load ticket details.");
      } finally {
        setLoading(false);
      }
    };

    fetchTicket();
  }, [companyId, ticketId, slaPolicy]);

  const isTicketOwner = ticket?.submittedBy === user.uid;
  const isHelpdeskEmployee =
    ticket?.claimedBy === (user.displayName || user.name);

  // 🔹 who can override priority?
  const isEmployer =
    (user?.role && user.role !== "employee") || user?.isAdmin === true;
  const canEditPriority = isEmployer || isHelpdeskEmployee;

  const handleUpdateComment = async () => {
    if (!companyId || !user) return;

    try {
      setUpdating(true);
      const ticketRef = doc(db, `companies/${companyId}/tickets`, ticketId);
      await updateDoc(ticketRef, {
        helpdeskComment,
      });

      // 🔁 If this was the first agent activity, set first response
      try {
        if (slaPolicy) {
          await markFirstResponseIfNeeded(
            companyId,
            ticketId,
            ticket,
            slaPolicy
          );
        }
      } catch (e) {
        console.warn("Could not set first response:", e);
      }

      alert("Helpdesk comment updated!");
    } catch (err) {
      console.error("Error updating comment:", err.message);
      setError("Failed to update comment.");
    } finally {
      setUpdating(false);
    }
  };

  // 🔹 NEW: update priority only
  const handleUpdatePriority = async () => {
    if (!companyId || !user || !canEditPriority) return;

    try {
      setUpdating(true);
      setError("");

      const ticketRef = doc(db, `companies/${companyId}/tickets`, ticketId);
      await updateDoc(ticketRef, {
        priority,
      });

      setTicket((prev) => ({
        ...prev,
        priority,
      }));

      alert("Priority updated.");
    } catch (err) {
      console.error("Error updating priority:", err.message);
      setError("Failed to update priority.");
    } finally {
      setUpdating(false);
    }
  };

  // ✅ Claim the Ticket
  const handleClaimTicket = async () => {
    if (!companyId || !user) return;

    try {
      setUpdating(true);
      setError("");

      const ticketRef = doc(db, `companies/${companyId}/tickets`, ticketId);
      await updateDoc(ticketRef, {
        claimedBy: user.displayName || user.name, // Store the name
        status: "In Progress",
      });

      setTicket((prevTicket) => ({
        ...prevTicket,
        claimedBy: user.displayName || user.name,
        status: "In Progress",
      }));

      // 🔁 Mark first response if this is the first agent action
      try {
        if (slaPolicy) {
          await markFirstResponseIfNeeded(
            companyId,
            ticketId,
            { ...ticket, claimedBy: user.displayName || user.name },
            slaPolicy
          );
        }
      } catch (e) {
        console.warn("Could not set first response:", e);
      }

      alert("You have claimed this ticket!");
    } catch (err) {
      console.error("Error claiming ticket:", err.message);
      setError("Failed to claim ticket.");
    } finally {
      setUpdating(false);
    }
  };

  // ✅ Unclaim the Ticket
  const handleUnclaimTicket = async () => {
    if (!companyId || !user) return;

    try {
      setUpdating(true);
      setError("");

      const ticketRef = doc(db, `companies/${companyId}/tickets`, ticketId);
      await updateDoc(ticketRef, {
        claimedBy: null,
        status: "Open",
      });

      setTicket((prevTicket) => ({
        ...prevTicket,
        claimedBy: null,
        status: "Open",
      }));

      try {
        if (slaPolicy) {
          await ensureTicketSla(
            companyId,
            ticketId,
            { ...ticket, claimedBy: null, status: "Open" },
            slaPolicy
          );
        }
      } catch (e) {
        console.warn("ensureTicketSla (unclaim) failed:", e);
      }

      alert("You have unclaimed this ticket!");
    } catch (err) {
      console.error("Error unclaiming ticket:", err.message);
      setError("Failed to unclaim ticket.");
    } finally {
      setUpdating(false);
    }
  };

  // ✅ Resolve the Ticket
  const handleResolveTicket = async () => {
    if (!companyId || !user || !resolutionNotes) {
      alert("Please enter resolution notes before resolving the ticket.");
      return;
    }

    try {
      setUpdating(true);
      setError("");

      const ticketRef = doc(db, `companies/${companyId}/tickets`, ticketId);
      await updateDoc(ticketRef, {
        status: "Resolved",
        resolvedBy: user.displayName || user.name,
        resolvedAt: new Date(),
        resolutionNotes: resolutionNotes, // ✅ Store resolution notes in Firestore
      });

      // 🔁 Stamp whether resolution breached the SLA
      try {
        if (slaPolicy) {
          await markResolved(
            companyId,
            ticketId,
            { ...ticket, resolvedAt: new Date() },
            slaPolicy
          );
        }
      } catch (e) {
        console.warn("Could not stamp resolution breach:", e);
      }

      setTicket((prevTicket) => ({
        ...prevTicket,
        status: "Resolved",
        resolvedBy: user.displayName || user.name,
        resolvedAt: new Date(),
        resolutionNotes: resolutionNotes, // ✅ Update the local state
      }));

      alert("Ticket has been marked as resolved with resolution notes!");
    } catch (err) {
      console.error("Error resolving ticket:", err.message);
      setError("Failed to resolve ticket.");
    } finally {
      setUpdating(false);
    }
  };

  const handleSaveResolutionNotes = async () => {
    if (!companyId || !user || !resolutionNotes) {
      alert("Please enter resolution notes before saving.");
      return;
    }

    try {
      setUpdating(true);
      setError("");

      const ticketRef = doc(db, `companies/${companyId}/tickets`, ticketId);
      await updateDoc(ticketRef, {
        resolutionNotes: resolutionNotes, // ✅ Save only resolution notes
      });

      alert("Resolution notes saved successfully!");
    } catch (err) {
      console.error("Error saving resolution notes:", err.message);
      setError("Failed to save resolution notes.");
    } finally {
      setUpdating(false);
    }
  };

  // ✅ Reopen the Ticket
  const handleReopenTicket = async () => {
    if (!companyId || !user) return;

    try {
      setUpdating(true);
      setError("");

      const ticketRef = doc(db, `companies/${companyId}/tickets`, ticketId);
      await updateDoc(ticketRef, {
        status: "Open",
        resolvedBy: null,
        resolvedAt: null,
      });

      setTicket((prevTicket) => ({
        ...prevTicket,
        status: "Open",
        resolvedBy: null,
        resolvedAt: null,
      }));

      // ✅ Recompute SLA due-times after reopen (resets clocks correctly)
      try {
        if (slaPolicy) {
          await ensureTicketSla(
            companyId,
            ticketId,
            { ...ticket, status: "Open", resolvedBy: null, resolvedAt: null },
            slaPolicy
          );
        }
      } catch (e) {
        console.warn("ensureTicketSla (reopen) failed:", e);
      }

      alert("Ticket has been reopened!");
    } catch (err) {
      console.error("Error reopening ticket:", err.message);
      setError("Failed to reopen ticket.");
    } finally {
      setUpdating(false);
    }
  };

  // ✅ Prevent Errors by Only Rendering Ticket Data When Available
  if (loading) return <p className="loading-message">Loading...</p>;
  if (error) return <p className="error-message">{error}</p>;
  if (!ticket) return <p>No ticket details found.</p>;

  return (
    <div className="ticket-details">
      <div className="ticket-details-topbar">
        <Link to="/submitted-tickets" className="back-button">
          ← Back to Tickets
        </Link>
      </div>

      <h2>Ticket Details</h2>

      {/* ===== GENERAL INFORMATION ===== */}
      <section className="ticket-section">
        <div className="ticket-section-header">
          <h3 className="ticket-section-title">General Information</h3>
          <p className="ticket-section-caption">
            Key context for this incident at a glance.
          </p>
        </div>

        <div className="ticket-info">
          <p>
            <strong>Incident No:</strong> {ticket?.incidentNo}
          </p>
          <p>
            <strong>Title:</strong> {ticket?.title}
          </p>
          <p>
            <strong>Description:</strong> {ticket?.description}
          </p>
          <p>
            <strong>Impact:</strong> {ticket?.impact}
          </p>
          <p>
            <strong>Urgency:</strong> {ticket?.urgency}
          </p>
          <p>
            <strong>Status:</strong>{" "}
            <span
              className={`status-pill status-${(ticket?.status || "")
                .toLowerCase()
                .replace(/\s+/g, "-")}`}
            >
              {ticket?.status}
            </span>
          </p>

          <p>
            <strong>Priority:</strong>{" "}
            {canEditPriority ? (
              <select
                className="td-priority-select"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                disabled={updating}
                title="Adjust ticket priority"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            ) : (
              <span
                className={`priority-pill small ${(ticket?.priority || "low")
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
              >
                {ticket?.priority || "Low"}
              </span>
            )}
          </p>

          <p>
            <strong>Submitted By:</strong>{" "}
            {ticket?.submittedByName || ticket?.submittedBy}
          </p>

          <p>
            <strong>Assigned To:</strong> {ticket?.claimedBy || "Not Claimed"}
          </p>
          <p>
            <strong>Resolved By:</strong> {ticket?.resolvedBy || "Not Resolved"}
          </p>
          {ticket?.resolvedAt && (
            <p>
              <strong>Resolved At:</strong>{" "}
              {ticket.resolvedAt.toDate
                ? new Date(ticket.resolvedAt.toDate()).toLocaleString()
                : new Date(ticket.resolvedAt).toLocaleString()}
            </p>
          )}
          {ticket?.screenshotURL && (
            <p>
              <strong>Screenshot:</strong>{" "}
              <a
                href={ticket.screenshotURL}
                target="_blank"
                rel="noopener noreferrer"
              >
                View Screenshot
              </a>
            </p>
          )}
        </div>

        {canEditPriority && (
          <div className="ticket-priority-actions">
            <button
              onClick={handleUpdatePriority}
              disabled={updating}
              className="btn btn-outline priority-save-button"
            >
              {updating ? "Saving…" : "Save Priority"}
            </button>
          </div>
        )}
      </section>

      {/* ===== SLA SUMMARY ===== */}
      {ticket?.sla && (
        <section className="ticket-section">
          <div className="ticket-section-header">
            <h3 className="ticket-section-title">SLA</h3>
            <p className="ticket-section-caption">
              Live view of first response and resolution targets.
            </p>
          </div>

          <div className="sla-mini">
            {/* First response row */}
            <div className="sla-mini-row">
              <div className="sla-mini-label">First response</div>
              <div className="sla-mini-value">
                {(() => {
                  const frAt = ticket.sla.firstResponseAt;
                  const frDue = ticket.sla.firstResponseDueAt;
                  const isBreachedFlag =
                    ticket.sla?.breachedFirstResponse === true;

                  const msOf = (x) => {
                    if (!x) return NaN;
                    if (typeof x?.toDate === "function")
                      return x.toDate().getTime();
                    if (x instanceof Date) return x.getTime();
                    return new Date(x).getTime();
                  };

                  if (frAt) {
                    const breachedByTime =
                      Number.isFinite(msOf(frAt)) &&
                      Number.isFinite(msOf(frDue)) &&
                      msOf(frAt) > msOf(frDue);

                    const breached = isBreachedFlag || breachedByTime;

                    return (
                      <span
                        className={`sla-mini-pill ${
                          breached ? "breach" : "ok"
                        }`}
                      >
                        {breached ? "Breached" : "Met"}
                      </span>
                    );
                  }

                  if (frDue) {
                    const late = Date.now() > msOf(frDue);
                    return (
                      <span
                        className={`sla-mini-pill ${late ? "breach" : "warn"}`}
                      >
                        {etaLabel(frDue)}
                      </span>
                    );
                  }

                  return "—";
                })()}
              </div>
            </div>

            {/* Resolution row */}
            <div className="sla-mini-row">
              <div className="sla-mini-label">Resolution</div>
              <div className="sla-mini-value">
                {ticket.resolvedAt ? (
                  <span
                    className={`sla-mini-pill ${
                      ticket.sla?.breachedResolution ? "breach" : "ok"
                    }`}
                  >
                    {ticket.sla?.breachedResolution ? "Breached" : "Met"}
                  </span>
                ) : ticket.sla.resolutionDueAt ? (
                  <span
                    className={`sla-mini-pill ${
                      Date.now() > _ms(ticket.sla.resolutionDueAt)
                        ? "breach"
                        : "warn"
                    }`}
                  >
                    {etaLabel(ticket.sla.resolutionDueAt)}
                  </span>
                ) : (
                  "—"
                )}
              </div>
            </div>

            {ticket.sla.paused && (
              <div className="sla-mini-paused">⏸ Timers paused</div>
            )}
          </div>
        </section>
      )}

      {/* ===== HELPDESK COMMENT ===== */}
      {isHelpdeskEmployee && ticket?.status !== "Resolved" && (
        <section className="ticket-section">
          <div className="ticket-section-header">
            <h3 className="ticket-section-title">Helpdesk Comment</h3>
            <p className="ticket-section-caption">
              Internal notes visible to the help desk team.
            </p>
          </div>

          <div className="helpdesk-comment">
            <label>Add/Edit Helpdesk Comment:</label>
            <textarea
              value={helpdeskComment}
              onChange={(e) => setHelpdeskComment(e.target.value)}
              placeholder="Enter a comment..."
            />
            <button
              onClick={handleUpdateComment}
              disabled={updating}
              className="btn btn-secondary"
            >
              {updating ? "Updating..." : "Update Comment"}
            </button>
          </div>
        </section>
      )}

      {/* ===== RESOLUTION NOTES ===== */}
      {isHelpdeskEmployee && (
        <section className="ticket-section">
          <div className="ticket-section-header">
            <h3 className="ticket-section-title">Resolution Notes</h3>
            <p className="ticket-section-caption">
              Document the fix you applied for audit and reporting.
            </p>
          </div>

          {ticket?.status !== "Resolved" ? (
            <div className="resolution-notes">
              <label>Add Resolution Notes:</label>
              <textarea
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                placeholder="Enter resolution notes..."
              />
              <button
                onClick={handleSaveResolutionNotes}
                disabled={updating}
                className="btn btn-outline"
              >
                {updating ? "Saving..." : "Save Notes"}
              </button>
            </div>
          ) : (
            <p className="resolution-readonly">
              <strong>Resolution Notes:</strong> {ticket?.resolutionNotes}
            </p>
          )}
        </section>
      )}

      {/* Ticket Owner: view-only feedback */}
      {isTicketOwner && ticket?.helpdeskComment && (
        <section className="ticket-section">
          <div className="ticket-section-header">
            <h3 className="ticket-section-title">Helpdesk Feedback</h3>
          </div>
          <p>
            <strong>Comment:</strong> {ticket.helpdeskComment}
          </p>
        </section>
      )}

      {/* ===== ACTIONS ===== */}
      <section className="ticket-section">
        <div className="ticket-section-header">
          <h3 className="ticket-section-title">Actions</h3>
          <p className="ticket-section-caption">
            Manage ownership, SLA state, and ticket resolution.
          </p>
        </div>

        <div className="ticket-actions-row">
          {ticket?.status === "Resolved" ? (
            <button
              className="btn btn-warning"
              onClick={handleReopenTicket}
              disabled={updating}
            >
              {updating ? "Reopening..." : "Reopen Ticket"}
            </button>
          ) : !ticket?.claimedBy ? (
            <button
              className="btn btn-primary"
              onClick={handleClaimTicket}
              disabled={updating}
            >
              {updating ? "Claiming..." : "Claim Ticket"}
            </button>
          ) : ticket?.claimedBy === (user.displayName || user.name) ? (
            <>
              <button
                className="btn btn-outline-danger"
                onClick={handleUnclaimTicket}
                disabled={updating}
              >
                {updating ? "Unclaiming..." : "Unclaim Ticket"}
              </button>
              <button
                className="btn btn-success"
                onClick={handleResolveTicket}
                disabled={updating}
              >
                {updating ? "Resolving..." : "Resolve Ticket"}
              </button>
            </>
          ) : (
            <p className="claimed-message">
              This ticket has been claimed by {ticket.claimedBy}.
            </p>
          )}
        </div>

        {(ticket?.sla?.policyTargets?.pauseWhenWaitingOnCustomer ??
          slaPolicy?.pauseWhenWaitingOnCustomer) && (
          <div className="ticket-sla-actions">
            <button
              className="btn btn-ghost"
              onClick={async () => {
                try {
                  const pauseStatus =
                    ticket?.sla?.policyTargets?.pauseStatus ??
                    slaPolicy?.pauseStatus ??
                    "Waiting on Customer";

                  const ticketRef = doc(
                    db,
                    `companies/${companyId}/tickets`,
                    ticketId
                  );
                  await updateDoc(ticketRef, { status: pauseStatus });

                  await enterPause(companyId, ticketId);

                  setTicket((prev) => ({
                    ...prev,
                    status: pauseStatus,
                    sla: {
                      ...(prev?.sla || {}),
                      paused: true,
                      pausedAt: new Date(),
                    },
                  }));
                } catch (e) {
                  console.error("Pause failed:", e);
                  setError("Failed to enter pause.");
                }
              }}
            >
              Ask Customer (pause SLA)
            </button>

            <button
              className="btn btn-secondary"
              onClick={async () => {
                try {
                  const ticketRef = doc(
                    db,
                    `companies/${companyId}/tickets`,
                    ticketId
                  );
                  await updateDoc(ticketRef, { status: "In Progress" });

                  await exitPause(companyId, ticketId, ticket, slaPolicy);

                  setTicket((prev) => ({
                    ...prev,
                    status: "In Progress",
                    sla: {
                      ...(prev?.sla || {}),
                      paused: false,
                      pausedAt: null,
                    },
                  }));
                } catch (e) {
                  console.error("Resume failed:", e);
                  setError("Failed to resume SLA timers.");
                }
              }}
            >
              Customer Replied (resume SLA)
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
